import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { m } from 'motion/react';
import { MapPin, Train, Plane, ShoppingCart, Scale, Stethoscope, GraduationCap, Shield, MoonStar } from 'lucide-react';
import type { MapPoint, DistanceEntry } from './locationData';
import { MAIN_LOCATION } from './locationData';

// Leaflet + react-leaflet ~150 KB. Ayrı chunk'ta tutulur ve harita
// görüş alanına girmeden indirilmez.
const LocationMap = lazy(() => import('./LocationMap'));

const distances = [
  {
    icon: Train,
    name: 'Gülsuyu Metro İstasyonu',
    secondaryName: 'Esenkent Metro İstasyonu',
    description: 'Yaya olarak yaklaşık yürüme mesafesi',
    distance: '4 dk',
    secondaryDistance: '8 dk',
    color: 'from-blue-500 to-blue-600',
    coords: [40.9236, 29.1551] as [number, number],
    secondaryCoords: [40.9212, 29.1654] as [number, number],
    zoom: 15,
  },
  {
    icon: Plane,
    name: 'Sabiha Gökçen Havalimanı',
    description: 'Araç ile yaklaşık mesafe',
    distance: '20 dk',
    color: 'from-sky-500 to-sky-600',
    coords: [40.8986, 29.3092] as [number, number],
    zoom: 13,
  },
  {
    icon: ShoppingCart,
    name: 'Piazza Alışveriş Merkezi',
    secondaryName: 'Ritim İstanbul Alışveriş Merkezi',
    description: 'Araç ile yaklaşık mesafe',
    distance: '5 dk',
    color: 'from-purple-500 to-purple-600',
    coords: [40.9195, 29.1674] as [number, number],
    secondaryCoords: [40.921854, 29.158569] as [number, number],
    zoom: 15,
  },
  {
    icon: Stethoscope,
    name: 'Lütfi Kırdar Şehir Hastanesi',
    description: 'Araç ile yaklaşık mesafe',
    distance: '8 dk',
    color: 'from-green-500 to-green-600',
    coords: [40.9160, 29.1705] as [number, number],
    zoom: 15,
  },
  {
    icon: GraduationCap,
    name: 'Maltepe Gökyüzü Koleji',
    secondaryName: 'Esenkent Ortaokulu',
    description: 'Yaya olarak yaklaşık yürüme mesafesi',
    distance: '4 dk',
    color: 'from-indigo-500 to-indigo-600',
    coords: [40.9250, 29.1627] as [number, number],
    secondaryCoords: [40.9261, 29.1639] as [number, number],
    zoom: 15,
  },
  {
    icon: MoonStar,
    name: 'Esenkent Merkez Cami',
    description: 'Yaya olarak yaklaşık yürüme mesafesi',
    distance: '3 dk',
    color: 'from-emerald-600 to-emerald-700',
    coords: [40.9252913, 29.1625701] as [number, number],
    zoom: 16,
    customMapIcon: 'mosque',
  },
  {
    icon: Shield,
    name: 'Şehit Saffet Okumuş Polis Merkezi Amirliği',
    description: 'Yaya olarak yaklaşık yürüme mesafesi',
    distance: '2 dk',
    color: 'from-blue-700 to-blue-800',
    coords: [40.9238, 29.1610] as [number, number],
    zoom: 16,
  },
  {
    icon: Scale,
    name: 'Anadolu Adliyesi',
    description: 'Araç ile yaklaşık mesafe',
    distance: '8 dk',
    color: 'from-green-500 to-green-600',
    coords: [40.9144, 29.1784] as [number, number],
    zoom: 15,
  },
];

const allLocations: MapPoint[] = [
  MAIN_LOCATION,
  ...distances.map((d) => ({ coords: d.coords, zoom: d.zoom, name: d.name })),
];

export function Location() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [flyTarget, setFlyTarget] = useState<MapPoint>(MAIN_LOCATION);

  // Harita chunk'ı ancak bölüm görüş alanına yaklaşınca indirilir
  const mapHostRef = useRef<HTMLDivElement>(null);
  const [mapVisible, setMapVisible] = useState(false);

  useEffect(() => {
    const el = mapHostRef.current;
    if (!el || mapVisible) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMapVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [mapVisible]);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setActiveIndex((prev) => {
        const next = (prev + 1) % allLocations.length;
        setFlyTarget(allLocations[next]);
        return next;
      });
    }, 4000);
    return () => clearInterval(id);
  }, [paused]);

  const activateCard = (distIndex: number, coords: [number, number], name: string, zoom: number) => {
    setActiveIndex(distIndex + 1);
    setFlyTarget({ coords, zoom, name });
    setPaused(true);
    setTimeout(() => setPaused(false), 12000);
  };

  const currentLocation = allLocations[activeIndex];
  const activeCardIndex = activeIndex - 1;

  return (
    <section id="location" className="py-24 bg-gray-50 dark:bg-slate-900">
      <div className="container mx-auto px-4 lg:px-8">
        <m.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-full text-sm tracking-wider mb-4">
            KONUM
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-slate-100 mb-6">
            Stratejik{' '}
            <span className="text-stone-700 dark:text-stone-300">Lokasyon</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            İstanbul'un kalbinde, tüm ulaşım hatlarına yakın, prestijli bir konumda
          </p>
        </m.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
          {/* Map */}
          <m.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="order-2 lg:order-1 h-full"
          >
            <div
              ref={mapHostRef}
              className="rounded-2xl shadow-xl overflow-hidden border border-gray-200 dark:border-slate-800 h-full min-h-[320px] relative isolate"
            >
              {mapVisible ? (
                <Suspense fallback={<div className="absolute inset-0 bg-slate-100 dark:bg-slate-800" />}>
                  <LocationMap
                    distances={distances as DistanceEntry[]}
                    flyTarget={flyTarget}
                    activeIndex={activeIndex}
                  />
                </Suspense>
              ) : (
                <div className="absolute inset-0 bg-slate-100 dark:bg-slate-800" aria-hidden="true" />
              )}

              {/* Google Maps link */}
              <a
                href="https://maps.app.goo.gl/vz5J7CjVj9Yr1zAv8?g_st=aw"
                target="_blank"
                rel="noopener noreferrer"
                className="absolute top-3 right-3 z-[1000] bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm text-slate-900 dark:text-white px-3 py-2 rounded-lg shadow-md text-sm font-medium hover:bg-white dark:hover:bg-slate-900 inline-flex items-center gap-2"
              >
                <MapPin size={14} />
                Google Maps'te Aç
              </a>

              {/* Alt şerit: konum etiketi + göstergeler.
                  Tek bir flex satırında duruyorlar; 9 gösterge 216px yer kapladığı
                  için dar haritalarda yan yana sığmazlar ve alt alta sarılırlar. */}
              <div className="absolute bottom-3 left-3 right-3 z-[1000] flex flex-wrap items-end justify-between gap-2">
                <m.div
                  key={currentLocation.name}
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="min-w-0 max-w-full bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm px-3 py-2 rounded-lg shadow-md text-sm font-semibold text-slate-800 dark:text-white flex items-center gap-2"
                >
                  <MapPin size={14} className="text-red-500 flex-shrink-0" />
                  <span className="truncate">{flyTarget.name}</span>
                  {activeIndex > 0 && (
                    <span className="ml-1 flex-shrink-0 px-2 py-0.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-full text-xs font-bold">
                      {distances[activeIndex - 1].distance}
                    </span>
                  )}
                </m.div>

                <div className="flex ml-auto rounded-lg bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm">
                  {allLocations.map((loc, i) => (
                    <button
                      key={loc.name}
                      type="button"
                      onClick={() => { setActiveIndex(i); setFlyTarget(allLocations[i]); setPaused(true); setTimeout(() => setPaused(false), 12000); }}
                      aria-label={`Haritada göster: ${loc.name}`}
                      aria-current={i === activeIndex}
                      className="flex items-center justify-center w-6 h-6"
                    >
                      <span
                        aria-hidden="true"
                        className={`block w-2 h-2 rounded-full transition-all duration-300 ${i === activeIndex ? 'bg-slate-900 dark:bg-white scale-125' : 'bg-slate-400 dark:bg-slate-600'}`}
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </m.div>

          {/* Distances */}
          <m.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="order-1 lg:order-2 h-full"
          >
            <div className="space-y-4 h-full flex flex-col">
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl p-6">
                <h3 className="text-2xl font-bold mb-2">Rain Nova</h3>
                <p className="text-gray-300 flex items-center gap-2">
                  <MapPin size={18} className="text-stone-400 flex-shrink-0" />
                  Esenkent, 34848 Maltepe / İstanbul
                </p>
              </div>

              <div className="space-y-3 flex-1">
                {distances.map((item, index) => (
                  <m.div
                    key={index}
                    onClick={'secondaryName' in item ? undefined : () => activateCard(index, item.coords, item.name, item.zoom)}
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.08 }}
                    className={`w-full flex items-center gap-4 rounded-xl p-4 transition-all duration-300 border text-left ${'secondaryName' in item ? '' : 'cursor-pointer'} ${
                      activeCardIndex === index
                        ? 'bg-slate-900 dark:bg-slate-700 text-white border-slate-700 shadow-xl scale-[1.02]'
                        : 'bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 border-gray-100 dark:border-slate-800 shadow-md hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-700'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${item.color} flex items-center justify-center flex-shrink-0`}>
                      <item.icon size={22} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      {'secondaryName' in item ? (
                        <div className="space-y-0.5">
                          <button
                            onClick={() => activateCard(index, item.coords, item.name, item.zoom)}
                            className={`w-full text-left flex items-center justify-between gap-2 hover:underline ${activeCardIndex === index ? 'text-white' : 'text-slate-900 dark:text-slate-100'}`}
                          >
                            <span className="font-semibold truncate">{item.name}</span>
                            {'secondaryDistance' in item && (
                              <span className="font-bold text-lg flex-shrink-0">{item.distance}</span>
                            )}
                          </button>
                          <button
                            onClick={() => activateCard(index, item.secondaryCoords, item.secondaryName, item.zoom)}
                            className={`w-full text-left flex items-center justify-between gap-2 hover:underline ${activeCardIndex === index ? 'text-white' : 'text-slate-900 dark:text-slate-100'}`}
                          >
                            <span className="font-semibold truncate">{item.secondaryName}</span>
                            {'secondaryDistance' in item && (
                              <span className="font-bold text-lg flex-shrink-0">{item.secondaryDistance}</span>
                            )}
                          </button>
                          {'secondaryDistance' in item && (
                            <p className={`text-xs text-right ${activeCardIndex === index ? 'text-slate-300' : 'text-gray-500'}`}>uzaklıkta</p>
                          )}
                        </div>
                      ) : (
                        <h4 className="font-semibold truncate">{item.name}</h4>
                      )}
                      <p className={`text-sm ${activeCardIndex === index ? 'text-slate-300' : 'text-gray-500 dark:text-gray-400'}`}>
                        {item.description}
                      </p>
                    </div>
                    {!('secondaryName' in item) || !('secondaryDistance' in item) ? (
                      <div className="text-right flex-shrink-0">
                        <div className="text-xl font-bold">{item.distance}</div>
                        <div className={`text-xs ${activeCardIndex === index ? 'text-slate-300' : 'text-gray-500'}`}>uzaklıkta</div>
                      </div>
                    ) : null}
                  </m.div>
                ))}
              </div>
            </div>
          </m.div>
        </div>
      </div>
    </section>
  );
}
